// Topics — the single vocabulary for "what we help with" and "how we work".
//
// This is the one place the taxonomy is defined. The blog index pill row, the
// article-page badge/CTA/related-posts logic and (in future) the concern hub
// pages all read from here. Renaming a label is a one-line change; nothing else
// needs touching because blogs store the topic *id*, never the label.
//
// IMPORTANT: this is a plain module with NO "use client". Server components
// import it for metadata and JSON-LD — re-exporting shared data through a
// client module makes .map() throw at build time.
//
// `blogs.json` -> `category` holds a TopicId (e.g. "gut-digestion"), not a
// display string. Resolve it with topicById() before rendering.

export type TopicGroup = "fix" | "how";

export interface Topic {
  id: string;
  /** Display label. Safe to change — nothing is keyed on it. */
  label: string;
  /** "fix" = a problem we solve, "how" = the method or format. */
  group: TopicGroup;
  /** Live route for this topic, if one exists yet. Concern hubs are not built. */
  href?: string;
  /** Article-page call to action. */
  cta: {
    text: string;
    link: string;
    btn: string;
    /**
     * Optional quieter second door under the main button.
     *
     * Recipes are the case this exists for: 11 posts all pointed at a bare
     * /shop, which is the weakest destination on the site for someone who has
     * just read about food. They now lead with the Bodhi Club — recipes are a
     * maintenance habit, and the Club is the maintenance offer — and keep the
     * standard free 15-minute discovery call as the no-commitment alternative,
     * the same pairing /nutrition and every ServiceCard already use.
     */
    secondary?: { link: string; btn: string };
  };
  bg: string;
  fg: string;
}

export const TOPICS: Topic[] = [
  // ── Things we fix ──────────────────────────────────────────────────────
  {
    id: "energy-fatigue",
    label: "Energy & Fatigue",
    group: "fix",
    href: "/energy/why-am-i-always-tired",
    cta: {
      text: "Tired of being tired? Book a free 15-minute discovery call and we'll work out what's draining you.",
      link: "/workwithme/discoverycall",
      btn: "Book Free Call",
    },
    bg: "var(--apricot-lt)",
    fg: "var(--coral-text)",
  },
  {
    id: "gut-digestion",
    label: "Gut & Digestion",
    group: "fix",
    cta: {
      text: "Ready to heal your gut? Book a free 15-minute discovery call and let's build a plan together.",
      link: "/workwithme/discoverycall",
      btn: "Book Free Call",
    },
    bg: "var(--mint)",
    fg: "var(--fern-dk)",
  },
  {
    id: "detox",
    label: "Detox & Toxic Load",
    group: "fix",
    cta: {
      text: "Give your body a proper reset — our Express Detox Pack bundles everything you need to start.",
      link: "/shop/bundles/express-detox-pack",
      btn: "See the Pack",
    },
    bg: "var(--mint)",
    fg: "var(--sage-dk)",
  },
  {
    id: "blood-sugar-weight",
    label: "Blood Sugar & Weight",
    group: "fix",
    href: "/metabolism/cant-lose-weight",
    cta: {
      text: "Blood sugar swings and stubborn weight usually have a root cause. Book a consultation to find yours.",
      link: "/workwithme/consultation",
      btn: "Book Consultation",
    },
    bg: "var(--peach-lt)",
    fg: "var(--coral-text)",
  },
  {
    id: "sleep-stress",
    label: "Sleep, Stress & Anxiety",
    group: "fix",
    cta: {
      text: "Looking for holistic support for anxiety or stress? Our retreats combine breathwork, cold immersion, yoga and nutrition guidance.",
      link: "/retreats",
      btn: "Explore Retreats",
    },
    bg: "var(--mist)",
    fg: "var(--ink-80)",
  },
  {
    id: "brain-fog-mood",
    label: "Brain Fog & Mood",
    group: "fix",
    href: "/brain/brain-fog-fix",
    cta: {
      text: "Can't think straight? Take the brain fog scorecard and see what's behind it.",
      link: "/brain/brain-fog-fix",
      btn: "Take the Scorecard",
    },
    bg: "var(--cream-dk)",
    fg: "var(--ink-80)",
  },
  {
    id: "hormones-thyroid",
    label: "Hormones & Thyroid",
    group: "fix",
    cta: {
      text: "Hormone and thyroid symptoms rarely travel alone. A full consultation looks at the whole picture.",
      link: "/workwithme/consultation",
      btn: "Book Consultation",
    },
    bg: "var(--peach-lt)",
    fg: "var(--burgundy)",
  },
  {
    id: "immunity-inflammation",
    label: "Immunity & Inflammation",
    group: "fix",
    cta: {
      text: "Support your immune system directly — our IV and injection clinic runs monthly.",
      link: "/dayclinic",
      btn: "See the Clinic",
    },
    bg: "var(--cream)",
    fg: "var(--fern-dk)",
  },
  {
    id: "fertility-pregnancy",
    label: "Fertility & Pregnancy",
    group: "fix",
    cta: {
      text: "Preparing for pregnancy, or already there? A personalised consultation covers both of you.",
      link: "/workwithme/consultation",
      btn: "Book Consultation",
    },
    bg: "var(--cream-dk)",
    fg: "var(--burgundy)",
  },
  {
    id: "childrens-health",
    label: "Children's Health",
    group: "fix",
    cta: {
      text: "Worried about your child's energy, digestion or immunity? Niki works with children of all ages.",
      link: "/workwithme/consultation",
      btn: "Book Consultation",
    },
    bg: "var(--peach-lt)",
    fg: "var(--honey-text)",
  },
  {
    id: "chronic-conditions",
    label: "Chronic & Complex Conditions",
    group: "fix",
    cta: {
      text: "Long-standing conditions need a systematic approach. The Farleigh Method changes one thing at a time and tracks what moves.",
      link: "/workwithme/farleighMethod",
      btn: "Explore the Method",
    },
    bg: "var(--cream)",
    fg: "var(--ink)",
  },
  {
    id: "cancer-support",
    label: "Cancer Support",
    group: "fix",
    cta: {
      text: "Naturopathic nutrition works alongside your oncology team, never instead of it. Book a consultation to talk it through.",
      link: "/workwithme/consultation",
      btn: "Book Consultation",
    },
    bg: "var(--mist)",
    fg: "var(--burgundy)",
  },

  // ── How we work ────────────────────────────────────────────────────────
  {
    id: "recipes",
    label: "Recipes & Everyday Eating",
    group: "how",
    cta: {
      text: "Eating like this every day is the whole point — the Bodhi Health Club is where that becomes a habit rather than a good week.",
      link: "/workwithme/align",
      btn: "Explore the Bodhi Club",
      secondary: { link: "/workwithme/discoverycall#schedule-call", btn: "Book a free 15-minute call" },
    },
    bg: "var(--apricot-lt)",
    fg: "var(--honey-text)",
  },
  {
    id: "functional-testing",
    label: "Functional Testing & Bloods",
    group: "how",
    href: "/nutrition/functional-testing",
    cta: {
      text: "Want help making sense of your blood results? Book a one-to-one consultation tailored to your health history.",
      link: "/workwithme/consultation",
      btn: "Book Consultation",
    },
    bg: "var(--mist)",
    fg: "var(--ink)",
  },
  {
    id: "iv-micronutrients",
    label: "IV & Micronutrients",
    group: "how",
    href: "/dayclinic",
    cta: {
      text: "Our monthly IV and injection clinic is medically supervised and tailored to you.",
      link: "/dayclinic",
      btn: "See the Clinic",
    },
    bg: "var(--cream)",
    fg: "var(--sage-dk)",
  },
  {
    id: "retreats-workshops",
    label: "Retreats & Workshops",
    group: "how",
    href: "/retreats",
    cta: {
      text: "Experience our Gut & Brain Retreats in the UK or Spain — nutrition, Wim Hof, breathwork, yoga and bespoke bodywork.",
      link: "/retreats",
      btn: "Find Out More",
    },
    bg: "var(--mist)",
    fg: "var(--fern-dk)",
  },
  {
    id: "naturopathy-explained",
    label: "Naturopathy Explained",
    group: "how",
    href: "/workwithme",
    cta: {
      text: "Take the first step. Book a free 15-minute discovery call to see how naturopathic nutrition can support your journey.",
      link: "/workwithme/discoverycall",
      btn: "Book Free Call",
    },
    bg: "var(--cream-dk)",
    fg: "var(--text-brand)",
  },
];

/** Fallback used when a blog has no category, or an id we no longer recognise. */
export const FALLBACK_TOPIC_ID = "naturopathy-explained";

const BY_ID = new Map(TOPICS.map((t) => [t.id, t]));

export function topicById(id: string | undefined | null): Topic {
  return (id ? BY_ID.get(id) : undefined) ?? BY_ID.get(FALLBACK_TOPIC_ID)!;
}

/**
 * Pills for a set of blogs, in TOPICS order, skipping topics with no posts.
 * Deriving rather than hardcoding is deliberate: the old blog index offered a
 * fixed list of seven, and "Retreats & Movement" filtered down to a single post.
 */
export function topicsForBlogs(blogs: { category?: string }[]): Topic[] {
  const present = new Set(blogs.map((b) => topicById(b.category).id));
  return TOPICS.filter((t) => present.has(t.id));
}
